import type { CSSProperties } from "react";
import { chartScaleGrayscaleStyle } from "@/components/charts/chart-scale";
import {
  MODULE_ACCENT_VAR,
  accentMarkColor,
  accentTokenStyle,
  normalizeAccent,
  seriesTokenStyle,
  statusTokenStyle,
} from "./module-accent";

/**
 * Maps the token names the modules consume onto the `--handyout-*` palette in
 * `app/handyout-tokens.css`. The values are `var()` references, not literals:
 * an inline declaration always wins the *name* against any class selector, but
 * its *value* is still substituted from the element's own cascade, so a theme
 * switch on an ancestor reaches the module through this alias. Pinning literals
 * here is what used to make the modules unthemeable.
 *
 * `--card`, `--bg`, `--emerald`, `--amber` and `--rose` are deliberately absent:
 * they carry the same meaning and the same light value as their `globals.css`
 * counterparts, so they inherit and theme for free.
 */
const SHARED: CSSProperties = {
  "--fg": "var(--handyout-fg)",
  "--muted": "var(--handyout-muted)",
  "--subtle": "var(--handyout-subtle)",
  "--border": "var(--handyout-border)",
  "--border-strong": "var(--handyout-border-strong)",
  "--border-dashed": "var(--handyout-border-dashed)",
  "--border-dashed-soft": "var(--handyout-border-dashed-soft)",
  "--border-dashed-strong": "var(--handyout-border-dashed-strong)",
  "--grid": "var(--handyout-grid)",
  "--grid-strong": "var(--handyout-grid-strong)",
  "--track": "var(--handyout-track)",
} as CSSProperties;

const COLORFUL: CSSProperties = {
  ...SHARED,
  "--chart-1": "var(--handyout-chart-1)",
  "--chart-2": "var(--handyout-chart-2)",
  "--chart-3": "var(--handyout-chart-3)",
  "--chart-4": "var(--handyout-chart-4)",
  "--chart-5": "var(--handyout-chart-5)",
  ...statusTokenStyle(false),
  [MODULE_ACCENT_VAR]: accentMarkColor(false),
} as CSSProperties;

const GRAYSCALE: CSSProperties = {
  ...SHARED,
  "--chart-1": "var(--handyout-chart-mono-1)",
  "--chart-2": "var(--handyout-chart-mono-2)",
  "--chart-3": "var(--handyout-chart-mono-3)",
  "--chart-4": "var(--handyout-chart-mono-4)",
  "--chart-5": "var(--handyout-chart-mono-5)",
  ...statusTokenStyle(true),
  [MODULE_ACCENT_VAR]: accentMarkColor(true),
} as CSSProperties;

const ACCENTED: CSSProperties[] = [];

/**
 * `grayscale` is a per-module choice — one module can be grayscale while its
 * neighbour is not — so the series selection stays an inline style.
 *
 * `accent` rotates the colourful series slots and sets `--module-accent` (see
 * `module-accent.ts`); it is ignored under `grayscale`, and while it is unset
 * the result is the plain scope.
 */
export function importsTokenScope(grayscale?: boolean, accent?: number | null): CSSProperties {
  if (grayscale) return GRAYSCALE;
  const slot = normalizeAccent(accent);
  if (slot === undefined) return COLORFUL;
  ACCENTED[slot] ??= {
    ...COLORFUL,
    ...accentTokenStyle(slot),
    ...statusTokenStyle(false, slot),
    [MODULE_ACCENT_VAR]: accentMarkColor(false, slot),
  } as CSSProperties;
  return ACCENTED[slot];
}

const CHART_GRAYSCALE_OVERRIDE = {
  "--chart-1": "var(--handyout-plot-mono-1)",
  "--chart-2": "var(--handyout-plot-mono-2)",
  "--chart-3": "var(--handyout-plot-mono-3)",
  "--chart-4": "var(--handyout-plot-mono-4)",
  "--chart-5": "var(--handyout-plot-mono-5)",
  ...chartScaleGrayscaleStyle(true),
} as CSSProperties;

/**
 * Neutrals for a module rendered with `grayscale`: the categorical series
 * colors and the sequential scale, so one call on a module root covers both.
 */
export function chartGrayscaleStyle(grayscale?: boolean): CSSProperties | undefined {
  return grayscale ? CHART_GRAYSCALE_OVERRIDE : undefined;
}

/**
 * `chartGrayscaleStyle` for the grayscale case; otherwise `--chart-1..5` pinned
 * to the handyout slots and rotated by the accent, so an author colour written
 * as `var(--chart-N)` shows the palette swatch the editor offered for it.
 */
export function chartPaletteStyle(grayscale?: boolean, accent?: number | null): CSSProperties {
  return grayscale ? CHART_GRAYSCALE_OVERRIDE : seriesTokenStyle(false, accent);
}
